<script lang="ts">
  import * as Select from '$lib/components/ui/select';
  import { createEventDispatcher } from 'svelte';
  import { X } from 'lucide-svelte';
  import { MONTHS, checkpointLabel, yearColor } from './seasons';

  export let startMonth: number;
  export let endMonth: number;
  export let yearsAvailable: number[];
  export let selectedYears: number[];
  // The year after the current one, when an XFAI forecast plan reaches into its season; null otherwise.
  export let nextYearAvailable: number | null = null;
  export let checkpointX: number | null = null;

  const dispatch = createEventDispatcher<{ months: { startMonth: number; endMonth: number }; years: number[]; clearCheckpoint: void }>();
  // Matches the server's limit on years per request.
  const MAX_YEARS = 12;
  const currentYear = new Date().getFullYear();
  const monthOptions = MONTHS.map((label, i) => ({ value: i + 1, label }));

  $: startSelected = monthOptions[startMonth - 1];
  $: endSelected = monthOptions[endMonth - 1];
  $: older = yearsAvailable.filter((y) => y < currentYear - 1);

  function toggleYear(year: number) {
    if (!selectedYears.includes(year) && selectedYears.length >= MAX_YEARS) return;
    const next = selectedYears.includes(year) ? selectedYears.filter((y) => y !== year) : [...selectedYears, year];
    dispatch('years', next.sort((a, b) => a - b));
  }
</script>

<!-- Every column is a heading over its control, and all columns share the same top edge so the headings line up. -->
<div class="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-6">
  <div class="flex flex-col gap-1">
    <span class="heading">Start month</span>
    <Select.Root
      portal="#shadcn-ui-dialog-target"
      items={monthOptions}
      selected={startSelected}
      onSelectedChange={(s) => s && dispatch('months', { startMonth: s.value, endMonth })}
    >
      <Select.Trigger class="w-[130px]"><Select.Value /></Select.Trigger>
      <Select.Content class="max-h-64 overflow-auto">
        {#each monthOptions as m}
          <Select.Item value={m.value}>{m.label}</Select.Item>
        {/each}
      </Select.Content>
    </Select.Root>
  </div>

  <div class="flex flex-col gap-1">
    <span class="heading">End month</span>
    <Select.Root
      portal="#shadcn-ui-dialog-target"
      items={monthOptions}
      selected={endSelected}
      onSelectedChange={(s) => s && dispatch('months', { startMonth, endMonth: s.value })}
    >
      <Select.Trigger class="w-[130px]"><Select.Value /></Select.Trigger>
      <Select.Content class="max-h-64 overflow-auto">
        {#each monthOptions as m}
          <Select.Item value={m.value}>{m.label}</Select.Item>
        {/each}
      </Select.Content>
    </Select.Root>
  </div>

  {#if checkpointX != null}
    <div class="flex flex-col gap-1">
      <span class="heading">Checkpoint</span>
      <!-- Same pill as the year buttons; the x says clicking it clears the checkpoint. -->
      <div class="flex h-9 items-center">
        <button class="year-chip selected" style="--year-color: #2563eb" title="Clear checkpoint" on:click={() => dispatch('clearCheckpoint')}>
          {checkpointLabel(checkpointX, startMonth)}
          <X size={12} />
        </button>
      </div>
    </div>
  {/if}

  <div class="flex flex-col gap-1">
    <span class="heading">Years</span>
    <div class="flex min-h-9 flex-wrap items-center gap-2">
      {#each [currentYear, currentYear - 1].filter((y) => yearsAvailable.includes(y)) as year}
        <button class="year-chip" class:selected={selectedYears.includes(year)} style="--year-color: {yearColor(year)}" on:click={() => toggleYear(year)}>
          <svg width="26" height="8" aria-hidden="true"><line x1="0" y1="4" x2="26" y2="4" stroke={yearColor(year)} stroke-width="2.5" /></svg>
          {year === currentYear ? 'Current year' : 'Last year'} - {year}
        </button>
      {/each}
      {#if nextYearAvailable != null}
        <!-- Dashed swatch: unlike the other pills, this year has no real activity history yet - it's wholly a
             projection from the current training plan's forecast. -->
        <button
          class="year-chip"
          class:selected={selectedYears.includes(nextYearAvailable)}
          style="--year-color: {yearColor(nextYearAvailable)}"
          title="Your training plan extends into {nextYearAvailable}. This shows its forecasted season."
          on:click={() => toggleYear(nextYearAvailable)}
        >
          <svg width="26" height="8" aria-hidden="true">
            <line x1="0" y1="4" x2="26" y2="4" stroke={yearColor(nextYearAvailable)} stroke-width="2.5" stroke-dasharray="3,2" />
          </svg>
          Next Year - {nextYearAvailable}
        </button>
      {/if}
      {#if older.length}
        <span class="text-xs text-muted-foreground">Choose year (up to {MAX_YEARS}):</span>
        {#each older as year}
          <button class="year-chip" class:selected={selectedYears.includes(year)} style="--year-color: {yearColor(year)}" on:click={() => toggleYear(year)}>
            <svg width="26" height="8" aria-hidden="true"><line x1="0" y1="4" x2="26" y2="4" stroke={yearColor(year)} stroke-width="2.5" /></svg>
            {year}
          </button>
        {/each}
      {/if}
    </div>
  </div>
</div>

<style>
  .heading {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 16px;
    color: hsl(var(--muted-foreground));
  }
  .year-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #d1d5db;
    border-radius: 9999px;
    padding: 2px 12px;
    font-size: 12px;
    color: #6b7280;
    background: transparent;
    opacity: 0.7;
  }
  /* Selected chips take the year's colour as an outline and tint; the swatch shows its line colour. */
  .year-chip.selected {
    border: 2px solid var(--year-color);
    background: color-mix(in srgb, var(--year-color) 12%, white);
    color: #111827;
    font-weight: 600;
    opacity: 1;
  }
</style>
